import { Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

const CHUNK_ERROR = /Loading chunk [\w-]+ failed|ChunkLoadError/;
const STORAGE_KEY = 'chunk_reload_at';
const RELOAD_WINDOW_MS = 60000;
const STALE_MESSAGE = 'A new version of Jobatory is available. Please refresh the page.';

/**
 * A deploy replaces the hashed JS files, so a tab opened before it fails to load
 * lazy routes. Reload once to pick up the new build; never loop.
 */
@Injectable({
  providedIn: 'root'
})
export class ChunkReloadService {
  constructor(private router: Router, private toastr: ToastrService) {}

  start(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        this.handle(event.error, event.url);
      }
    });
  }

  handle(error: any, url: string): void {
    const text = `${error?.name || ''} ${error?.message || error || ''}`;
    if (!CHUNK_ERROR.test(text)) {
      return;
    }
    // Without a saved marker a missing chunk would reload forever, so no marker means no reload.
    if (this.reloadedRecently() || !this.remember()) {
      this.toastr.error(STALE_MESSAGE, '', { disableTimeOut: true });
      return;
    }
    this.reloadTo(url);
  }

  protected reloadTo(url: string): void {
    window.location.assign(url);
  }

  private reloadedRecently(): boolean {
    try {
      const at = Number(sessionStorage.getItem(STORAGE_KEY));
      return at > 0 && Date.now() - at < RELOAD_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private remember(): boolean {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
      return true;
    } catch {
      return false;
    }
  }
}
